/**
 * System Integrity Audit Engine
 * 
 * Validates relationships, orphaned records, missing dependencies,
 * and inconsistent statuses across all modules.
 * Designed to be run on-demand or as a nightly self-check.
 */

import type { Job, Issue, Contractor, ContractorOpportunity, CustomerCredit } from "./types";
import type { Quote } from "./quoteTypes";
import { isWonStatus } from "@/data/quoteTypes";
import type { BuildReviewEvent } from "./buildReviewTypes";
import { deriveBuildReviewStatus } from "./buildReviewTypes";
import type { ServiceTicket } from "./serviceTicketTypes";
import type { PurchaseOrder, StagingRecord, LoadOutChecklist } from "./warehouseExecution";
import type { AppointmentResult } from "./appointmentResultTypes";

/* ── Types ── */

export type IntegritySeverity = "critical" | "warning" | "info";

export type IntegrityCategory =
  | "relationship"
  | "status"
  | "financial"
  | "document"
  | "readiness"
  | "orphan"
  | "blocker";

export interface IntegrityFinding {
  id: string;
  severity: IntegritySeverity;
  category: IntegrityCategory;
  title: string;
  detail: string;
  entityType: "job" | "quote" | "issue" | "contractor" | "credit" | "system";
  entityId?: string;
  actionLink?: string;
  recommendation: string;
}

export interface IntegrityReport {
  runAt: string;
  findings: IntegrityFinding[];
  summary: {
    total: number;
    critical: number;
    warning: number;
    info: number;
    passed: number;
    checksRun: number;
  };
}

/* ── Checks ── */

let findingCounter = 0;
function fid(): string {
  return `integrity-${++findingCounter}`;
}

function checkQuoteIntegrity(quotes: Quote[], jobs: Job[]): IntegrityFinding[] {
  const findings: IntegrityFinding[] = [];

  // Build lookup map for jobs by ID
  const jobsById = new Map(jobs.map(j => [j.id, j]));

  quotes.forEach(q => {
    // Won quote without a linked job
    if ((isWonStatus(q.status)) && !q.convertedJobId) {
      findings.push({
        id: fid(), severity: "critical", category: "relationship",
        title: "Won Opportunity Without Job",
        detail: `Opportunity ${q.displayReference} (${q.customerName}) is marked Won but has no linked job.`,
        entityType: "quote", entityId: q.id, actionLink: `/opportunities/${q.id}`,
        recommendation: "Create a job from this quote or verify the conversion.",
      });
    }

    // Won quote referencing a non-existent job
    if (q.convertedJobId && !jobsById.has(q.convertedJobId)) {
      const referencedJob = q.convertedJobId;
      findings.push({
        id: fid(), severity: "critical", category: "orphan",
        title: "Opportunity References Missing Job",
        detail: `Opportunity ${q.displayReference} references job ${referencedJob} which does not exist.`,
        entityType: "quote", entityId: q.id, actionLink: `/opportunities/${q.id}`,
        recommendation: "Re-link the quote to the correct job or re-create the job.",
      });
    }

    // Won quote without a signed contract
    if (isWonStatus(q.status) && (!q.contract || q.contract.status !== "Signed")) {
      findings.push({
        id: fid(), severity: "warning", category: "document",
        title: "Won Opportunity Missing Signed Contract",
        detail: `Opportunity ${q.displayReference} is Won but contract is ${q.contract?.status || "missing"}.`,
        entityType: "quote", entityId: q.id, actionLink: `/opportunities/${q.id}`,
        recommendation: "Ensure the contract is fully signed before production.",
      });
    }

    // Quote with pricing but no guardrails
    if (q.totalEstimate > 0 && !q.pricingGuardrails) {
      findings.push({
        id: fid(), severity: "info", category: "financial",
        title: "Opportunity Missing Pricing Guardrails",
        detail: `Opportunity ${q.displayReference} has a total of $${q.totalEstimate.toLocaleString()} but no guardrails set.`,
        entityType: "quote", entityId: q.id, actionLink: `/opportunities/${q.id}`,
        recommendation: "Set pricing guardrails to protect margin integrity.",
      });
    }

    // Presented quote without verification
    if ((q.stage === "Appointment Completed" || isWonStatus(q.status)) && !q.verification?.verificationComplete) {
      findings.push({
        id: fid(), severity: "warning", category: "blocker",
        title: "Opportunity Missing Customer Verification",
        detail: `Opportunity ${q.displayReference} is ${q.status} but customer verification is incomplete.`,
        entityType: "quote", entityId: q.id, actionLink: `/opportunities/${q.id}`,
        recommendation: "Complete customer and property verification.",
      });
    }
  });

  return findings;
}

function checkJobIntegrity(jobs: Job[], quotes: Quote[], issues: Issue[]): IntegrityFinding[] {
  const findings: IntegrityFinding[] = [];

  jobs.forEach(job => {
    const jobRef = job.jobNumber ?? job.id;

    // Job with no source quote
    const sourceQuote = quotes.find(q => q.convertedJobId === job.id);
    if (!sourceQuote) {
      findings.push({
        id: fid(), severity: "info", category: "relationship",
        title: "Job Without Source Quote",
        detail: `${jobRef} (${job.customerName}) has no linked source quote.`,
        entityType: "job", entityId: job.id, actionLink: `/jobs/${job.id}`,
        recommendation: "Verify this job was created through the proper quote workflow.",
      });
    }

    // Scheduled without signed contract
    if (job.scheduledInstallDate && job.currentStage !== "Completed") {
      const q = quotes.find(q2 => q2.convertedJobId === job.id);
      if (q && (!q.contract || q.contract.status !== "Signed")) {
        findings.push({
          id: fid(), severity: "warning", category: "document",
          title: "Scheduled Without Signed Contract",
          detail: `${jobRef} is scheduled for ${job.scheduledInstallDate} but contract is not signed.`,
          entityType: "job", entityId: job.id, actionLink: `/jobs/${job.id}`,
          recommendation: "Ensure contract is signed before the install date.",
        });
      }
    }

    // Permit required but not approved on active job
    if (
      job.permitRequired &&
      job.permitStatus !== "Approved" &&
      job.permitStatus !== "Not Required" &&
      !["Completed", "Sale Closed"].includes(job.currentStage)
    ) {
      if (job.scheduledInstallDate) {
        findings.push({
          id: fid(), severity: "warning", category: "readiness",
          title: "Scheduled Without Permit Approval",
          detail: `${jobRef} is scheduled but permit status is "${job.permitStatus}".`,
          entityType: "job", entityId: job.id, actionLink: `/jobs/${job.id}`,
          recommendation: "Obtain permit approval before the install date.",
        });
      }
    }

    // Note: contractValue ≠ payoutAmount is EXPECTED — they're different concepts.
    // contractValue = customer contract; payoutAmount = contractor install pay.
    // Drift is only suspicious when payoutAmount EXCEEDS contractValue (margin loss).
    if (
      job.financials &&
      job.payoutAmount > 0 &&
      job.financials.contractValue > 0 &&
      job.payoutAmount > job.financials.contractValue
    ) {
      findings.push({
        id: fid(), severity: "warning", category: "financial",
        title: "Contractor Payout Exceeds Contract Value",
        detail: `${job.id} contractor payout ($${job.payoutAmount.toLocaleString()}) exceeds customer contract value ($${job.financials.contractValue.toLocaleString()}).`,
        entityType: "job", entityId: job.id, actionLink: `/jobs/${job.id}`,
        recommendation: "Review change orders and contractor pay profile.",
      });
    }

    // Completed without QA
    if (job.currentStage === "Completed" && !job.qaSubmitted) {
      findings.push({
        id: fid(), severity: "critical", category: "status",
        title: "Completed Without QA Submission",
        detail: `${jobRef} is marked Completed but QA was never submitted.`,
        entityType: "job", entityId: job.id, actionLink: `/jobs/${job.id}`,
        recommendation: "Submit the QA form or revert the job stage.",
      });
    }

    // Rescission active but job advanced past it
    if (
      job.rescissionStatus === "In Rescission" &&
      ["Scheduled", "In Progress", "QA Review", "Completed"].includes(job.currentStage)
    ) {
      findings.push({
        id: fid(), severity: "critical", category: "blocker",
        title: "Job Advanced During Rescission",
        detail: `${jobRef} is in "${job.currentStage}" but rescission period is still active.`,
        entityType: "job", entityId: job.id, actionLink: `/jobs/${job.id}`,
        recommendation: "Wait for rescission to clear before advancing.",
      });
    }

    // Unassigned but dispatched
    if (job.assignmentType === "Unassigned" && ["Assigned", "Accepted"].includes(job.dispatchStatus)) {
      findings.push({
        id: fid(), severity: "warning", category: "status",
        title: "Dispatched Without Assignment",
        detail: `${jobRef} dispatch is "${job.dispatchStatus}" but no crew/sub is assigned.`,
        entityType: "job", entityId: job.id, actionLink: `/jobs/${job.id}`,
        recommendation: "Assign a crew or subcontractor before dispatch.",
      });
    }
  });

  return findings;
}

function checkIssueIntegrity(issues: Issue[], jobs: Job[]): IntegrityFinding[] {
  const findings: IntegrityFinding[] = [];
  const jobIds = new Set(jobs.map(j => j.id));

  issues.forEach(issue => {
    // Issue referencing non-existent job
    if (!jobIds.has(issue.jobId)) {
      findings.push({
        id: fid(), severity: "warning", category: "orphan",
        title: "Issue References Missing Job",
        detail: `Issue ${issue.id} references job ${issue.jobId} which does not exist.`,
        entityType: "issue", entityId: issue.id,
        recommendation: "Re-link the issue to the correct job or archive it.",
      });
    }

    // Resolved issue without resolution notes
    if (issue.status === "Resolved" && !issue.resolutionNotes) {
      findings.push({
        id: fid(), severity: "info", category: "document",
        title: "Resolved Issue Missing Notes",
        detail: `Issue ${issue.id} (${issue.issueType}) is resolved but has no resolution notes.`,
        entityType: "issue", entityId: issue.id, actionLink: `/jobs/${issue.jobId}`,
        recommendation: "Add resolution notes for audit trail completeness.",
      });
    }
  });

  return findings;
}

function checkCreditIntegrity(credits: CustomerCredit[], jobs: Job[], issues: Issue[]): IntegrityFinding[] {
  const findings: IntegrityFinding[] = [];
  const jobIds = new Set(jobs.map(j => j.id));
  const issueIds = new Set(issues.map(i => i.id));

  credits.forEach(cr => {
    // Credit referencing non-existent job
    if (!jobIds.has(cr.jobId)) {
      findings.push({
        id: fid(), severity: "warning", category: "orphan",
        title: "Credit References Missing Job",
        detail: `Credit ${cr.id} ($${cr.creditAmount}) references job ${cr.jobId} which does not exist.`,
        entityType: "credit", entityId: cr.id,
        recommendation: "Re-link the credit to the correct job.",
      });
    }

    // Credit linked to issue that doesn't exist
    if (cr.issueId && !issueIds.has(cr.issueId)) {
      findings.push({
        id: fid(), severity: "info", category: "orphan",
        title: "Credit References Missing Issue",
        detail: `Credit ${cr.id} references issue ${cr.issueId} which does not exist.`,
        entityType: "credit", entityId: cr.id,
        recommendation: "Verify the issue linkage or clear the reference.",
      });
    }

    // Draft credit with no attribution
    if (cr.status === "Draft" && !cr.attributionType) {
      findings.push({
        id: fid(), severity: "info", category: "financial",
        title: "Pending Credit Without Attribution",
        detail: `Credit ${cr.id} ($${cr.creditAmount}) is pending but has no attribution type.`,
        entityType: "credit", entityId: cr.id, actionLink: `/jobs/${cr.jobId}`,
        recommendation: "Set an attribution type to track responsibility.",
      });
    }
  });

  return findings;
}

function checkContractorIntegrity(contractors: Contractor[], opportunities: ContractorOpportunity[], jobs: Job[]): IntegrityFinding[] {
  const findings: IntegrityFinding[] = [];

  // Opportunities referencing non-existent contractors
  const contractorIds = new Set(contractors.map(c => c.id));
  opportunities.forEach(opp => {
    if (!contractorIds.has(opp.contractorId)) {
      findings.push({
        id: fid(), severity: "warning", category: "orphan",
        title: "Opportunity References Missing Contractor",
        detail: `Opportunity ${opp.id} references contractor ${opp.contractorId} which does not exist.`,
        entityType: "contractor", entityId: opp.contractorId,
        recommendation: "Archive the opportunity or re-link to the correct contractor.",
      });
    }
  });

  // Active contractors with no jobs ever assigned
  contractors.filter(c => c.active).forEach(c => {
    const hasJob = jobs.some(j => j.assignedSubcontractor === c.companyName);
    const hasOpp = opportunities.some(o => o.contractorId === c.id);
    if (!hasJob && !hasOpp) {
      findings.push({
        id: fid(), severity: "info", category: "relationship",
        title: "Active Contractor With No Activity",
        detail: `${c.companyName} is active but has never been assigned or sent an opportunity.`,
        entityType: "contractor", entityId: c.id, actionLink: `/contractors/${c.id}`,
        recommendation: "Verify contractor status or consider deactivation.",
      });
    }
  });

  return findings;
}

function checkSystemConsistency(jobs: Job[], quotes: Quote[]): IntegrityFinding[] {
  const findings: IntegrityFinding[] = [];

  // Duplicate job IDs
  const jobIdCounts = new Map<string, number>();
  jobs.forEach(j => jobIdCounts.set(j.id, (jobIdCounts.get(j.id) || 0) + 1));
  jobIdCounts.forEach((count, id) => {
    if (count > 1) {
      findings.push({
        id: fid(), severity: "critical", category: "status",
        title: "Duplicate Job ID",
        detail: `Job ID "${id}" appears ${count} times in the system.`,
        entityType: "system",
        recommendation: "Investigate and resolve the duplicate records.",
      });
    }
  });

  // Duplicate quote IDs
  const quoteIdCounts = new Map<string, number>();
  quotes.forEach(q => quoteIdCounts.set(q.id, (quoteIdCounts.get(q.id) || 0) + 1));
  quoteIdCounts.forEach((count, id) => {
    if (count > 1) {
      findings.push({
        id: fid(), severity: "critical", category: "status",
        title: "Duplicate Quote ID",
        detail: `Quote ID "${id}" appears ${count} times in the system.`,
        entityType: "system",
        recommendation: "Investigate and resolve the duplicate records.",
      });
    }
  });

  return findings;
}

function checkBuildReviewIntegrity(buildReviewEvents: BuildReviewEvent[], jobs: Job[]): IntegrityFinding[] {
  const findings: IntegrityFinding[] = [];

  // Group events by jobId — last event determines current status
  const eventsByJob = new Map<string, BuildReviewEvent[]>();
  buildReviewEvents.forEach(ev => {
    const list = eventsByJob.get(ev.jobId) ?? [];
    list.push(ev);
    eventsByJob.set(ev.jobId, list);
  });

  const STAGES_PAST_SURVEY = [
    "Material Ordered", "Material Received", "Install Scheduled", "Crew Assigned",
    "Install In Progress", "Install Complete", "QA Review", "QA Approved",
    "Final Payment Collected", "Permit Closed", "Project Closed", "Completed",
  ];
  const STAGES_INSTALL_OR_LATER = [
    "Install Scheduled", "Crew Assigned", "Install In Progress",
    "Install Complete", "QA Review", "QA Approved",
  ];

  jobs.forEach(job => {
    if (job.currentStage === "Completed") return;
    const jobRef = job.jobNumber ?? job.id;
    const events = eventsByJob.get(job.id) ?? [];
    const status = deriveBuildReviewStatus(events);

    // Job past site survey with no build review started
    if (STAGES_PAST_SURVEY.includes(job.currentStage) && events.length === 0) {
      findings.push({
        id: fid(), severity: "warning", category: "readiness",
        title: "No Build Review Submitted",
        detail: `${jobRef} (${job.customerName}) is at "${job.currentStage}" but has no build review on record.`,
        entityType: "job", entityId: job.id, actionLink: `/jobs/${job.id}`,
        recommendation: "Submit a build review before materials are ordered.",
      });
    }

    // Build review stuck waiting on sales action
    if (status === "On Hold — Sales Action Required") {
      findings.push({
        id: fid(), severity: "critical", category: "blocker",
        title: "Build Review On Hold — Sales Action Required",
        detail: `${jobRef} (${job.customerName}) build review is blocked waiting for sales confirmation.`,
        entityType: "job", entityId: job.id, actionLink: `/jobs/${job.id}`,
        recommendation: "Sales rep must confirm or revise the build before production can continue.",
      });
    }

    // Job at install stage or later without an approved build review
    if (
      STAGES_INSTALL_OR_LATER.includes(job.currentStage) &&
      status !== "Approved" &&
      status !== "Final Review"
    ) {
      findings.push({
        id: fid(), severity: "critical", category: "readiness",
        title: "Install Staged Without Approved Build Review",
        detail: `${jobRef} is at "${job.currentStage}" but build review status is "${status}".`,
        entityType: "job", entityId: job.id, actionLink: `/jobs/${job.id}`,
        recommendation: "Obtain build review approval before scheduling or dispatching crew.",
      });
    }
  });

  return findings;
}

function checkServiceTicketIntegrity(serviceTickets: ServiceTicket[], jobs: Job[]): IntegrityFinding[] {
  const findings: IntegrityFinding[] = [];
  const jobIds = new Set(jobs.map(j => j.id));
  const now = Date.now();
  const MS_48H = 48 * 60 * 60 * 1000;

  serviceTickets.forEach(ticket => {
    // Ticket referencing a job that doesn't exist
    if (!jobIds.has(ticket.jobId)) {
      findings.push({
        id: fid(), severity: "warning", category: "orphan",
        title: "Service Ticket References Missing Job",
        detail: `Ticket ${ticket.id} ("${ticket.subject}") references job ${ticket.jobId} which does not exist.`,
        entityType: "job", entityId: ticket.jobId,
        recommendation: "Re-link the ticket to the correct job or close it.",
      });
    }

    // Urgent ticket open for over 48 hours without being scheduled
    if (
      ticket.priority === "Urgent" &&
      !["Scheduled", "Resolved", "Closed"].includes(ticket.status) &&
      now - new Date(ticket.createdAt).getTime() > MS_48H
    ) {
      findings.push({
        id: fid(), severity: "critical", category: "blocker",
        title: "Urgent Ticket Unscheduled Past 48 Hours",
        detail: `Ticket ${ticket.id} ("${ticket.subject}") is Urgent and has been open for over 48 hours without scheduling.`,
        entityType: "job", entityId: ticket.jobId, actionLink: `/service-tickets`,
        recommendation: "Schedule this ticket immediately or escalate to the operations manager.",
      });
    }

    // Resolved ticket with no resolution notes
    if (ticket.status === "Resolved" && !ticket.resolution) {
      findings.push({
        id: fid(), severity: "info", category: "document",
        title: "Resolved Ticket Missing Resolution Notes",
        detail: `Ticket ${ticket.id} ("${ticket.subject}") is marked Resolved but has no resolution notes.`,
        entityType: "job", entityId: ticket.jobId, actionLink: `/service-tickets`,
        recommendation: "Add resolution notes for warranty and audit trail completeness.",
      });
    }
  });

  return findings;
}

function checkWarehouseIntegrity(
  purchaseOrders: PurchaseOrder[],
  stagingRecords: StagingRecord[],
  loadOutChecklists: LoadOutChecklist[],
  jobs: Job[],
): IntegrityFinding[] {
  const findings: IntegrityFinding[] = [];

  const poJobIds = new Set(purchaseOrders.flatMap(po => po.jobIds ?? [po.jobId]));
  const stagingByJob = new Map(stagingRecords.map(s => [s.jobId, s]));
  const loadOutByJob = new Map(loadOutChecklists.map(l => [l.jobId, l]));
  const now = Date.now();
  const MS_3_DAYS = 3 * 24 * 60 * 60 * 1000;

  jobs.forEach(job => {
    const jobRef = job.jobNumber ?? job.id;

    // Job with a scheduled install date but no PO created
    if (
      job.scheduledInstallDate &&
      !["Completed", "Sale Closed", "Deposit Collected"].includes(job.currentStage) &&
      !poJobIds.has(job.id)
    ) {
      findings.push({
        id: fid(), severity: "warning", category: "readiness",
        title: "Scheduled Install With No Purchase Order",
        detail: `${jobRef} (${job.customerName}) is scheduled for ${job.scheduledInstallDate} but has no purchase order on record.`,
        entityType: "job", entityId: job.id, actionLink: `/jobs/${job.id}`,
        recommendation: "Create a purchase order before the install date.",
      });
    }

    // Install imminent (within 3 days) but staging not complete
    if (job.scheduledInstallDate) {
      const installMs = new Date(job.scheduledInstallDate).getTime();
      const staging = stagingByJob.get(job.id);
      if (
        installMs - now <= MS_3_DAYS &&
        installMs > now &&
        (!staging || staging.status !== "Fully Staged")
      ) {
        findings.push({
          id: fid(), severity: "warning", category: "readiness",
          title: "Install Within 3 Days — Materials Not Fully Staged",
          detail: `${jobRef} installs on ${job.scheduledInstallDate} but staging status is "${staging?.status ?? "Not Staged"}".`,
          entityType: "job", entityId: job.id, actionLink: `/warehouse`,
          recommendation: "Complete material staging in the warehouse before dispatch.",
        });
      }
    }

    // Dispatched or in-progress job with incomplete load-out checklist
    if (["Crew Assigned", "Install In Progress"].includes(job.currentStage)) {
      const loadOut = loadOutByJob.get(job.id);
      if (!loadOut || !loadOut.complete) {
        findings.push({
          id: fid(), severity: "critical", category: "readiness",
          title: "Job Dispatched Without Completed Load-Out Checklist",
          detail: `${jobRef} (${job.customerName}) is "${job.currentStage}" but the load-out checklist is incomplete.`,
          entityType: "job", entityId: job.id, actionLink: `/warehouse`,
          recommendation: "Complete the load-out checklist before the crew departs.",
        });
      }
    }
  });

  return findings;
}

function checkAppointmentResultIntegrity(appointmentResults: AppointmentResult[], jobs: Job[]): IntegrityFinding[] {
  const findings: IntegrityFinding[] = [];
  const now = Date.now();
  const MS_3_DAYS = 3 * 24 * 60 * 60 * 1000;

  appointmentResults.forEach(result => {
    // Sold result with next action still pending after 3 days
    if (
      result.resultType === "sold" &&
      result.nextActionStatus === "pending" &&
      now - new Date(result.submittedAt).getTime() > MS_3_DAYS
    ) {
      const linkedJob = jobs.find(j => j.id === result.opportunityId || j.customerName === result.customerName);
      findings.push({
        id: fid(), severity: "warning", category: "readiness",
        title: "Sold Appointment — Next Action Stalled",
        detail: `Appointment result for ${result.customerName} (rep: ${result.assignedRep}) was marked Sold but the next action has been pending for over 3 days.`,
        entityType: "job", entityId: linkedJob?.id,
        actionLink: linkedJob ? `/jobs/${linkedJob.id}` : `/appointment-results`,
        recommendation: "Advance the job through the deposit and job creation workflow.",
      });
    }

    // Rescheduled result with no reschedule date set
    if (result.resultType === "rescheduled" && !result.rescheduleDatetime) {
      findings.push({
        id: fid(), severity: "warning", category: "status",
        title: "Rescheduled Appointment With No New Date",
        detail: `Appointment for ${result.customerName} (rep: ${result.assignedRep}) is marked Rescheduled but no new appointment date was recorded.`,
        entityType: "system",
        actionLink: `/appointment-results`,
        recommendation: "Set a reschedule date to prevent the lead from going cold.",
      });
    }
  });

  return findings;
}

/* ── Main Runner ── */

const TOTAL_CHECK_GROUPS = 10;

export function runIntegrityAudit(
  jobs: Job[],
  quotes: Quote[],
  issues: Issue[],
  credits: CustomerCredit[],
  contractors: Contractor[],
  opportunities: ContractorOpportunity[],
  buildReviewEvents: BuildReviewEvent[],
  serviceTickets: ServiceTicket[],
  purchaseOrders: PurchaseOrder[],
  stagingRecords: StagingRecord[],
  loadOutChecklists: LoadOutChecklist[],
  appointmentResults: AppointmentResult[],
): IntegrityReport {
  findingCounter = 0;

  const allFindings: IntegrityFinding[] = [
    ...checkQuoteIntegrity(quotes, jobs),
    ...checkJobIntegrity(jobs, quotes, issues),
    ...checkIssueIntegrity(issues, jobs),
    ...checkCreditIntegrity(credits, jobs, issues),
    ...checkContractorIntegrity(contractors, opportunities, jobs),
    ...checkSystemConsistency(jobs, quotes),
    ...checkBuildReviewIntegrity(buildReviewEvents, jobs),
    ...checkServiceTicketIntegrity(serviceTickets, jobs),
    ...checkWarehouseIntegrity(purchaseOrders, stagingRecords, loadOutChecklists, jobs),
    ...checkAppointmentResultIntegrity(appointmentResults, jobs),
  ];

  // Sort by severity
  const severityOrder: Record<IntegritySeverity, number> = { critical: 0, warning: 1, info: 2 };
  allFindings.sort((a, b) => severityOrder[a.severity] - severityOrder[b.severity]);

  const critical = allFindings.filter(f => f.severity === "critical").length;
  const warning = allFindings.filter(f => f.severity === "warning").length;
  const info = allFindings.filter(f => f.severity === "info").length;

  return {
    runAt: new Date().toISOString(),
    findings: allFindings,
    summary: {
      total: allFindings.length,
      critical,
      warning,
      info,
      passed: TOTAL_CHECK_GROUPS - (critical > 0 ? 1 : 0) - (warning > 0 ? 1 : 0),
      checksRun: TOTAL_CHECK_GROUPS,
    },
  };
}
